import { useState, useCallback } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Sparkles, PlayCircle, CheckCircle2, AlertCircle, Loader2, Database, Eye, Edit2, FileText, X, Target, Key, Search, Lock, History, Calendar, GitBranch, Network } from 'lucide-react';
import { api } from '../../lib/api';
import Button from '../ui/Button';
import Card, { CardHeader, CardBody, CardFooter } from '../ui/Card';
import Modal from '../ui/Modal';
import { useProgressStream } from '../../hooks/useProgressStream';
import ProgressDisplay from './ProgressDisplay';
import SchemaEditor from './SchemaEditor';
import { SchemaEvaluationResults } from './SchemaEvaluationResults';
import { SchemaVisualizer } from './SchemaVisualizer';
import { SchemaEvaluation, SchemaVersionsResponse } from '../../types';

interface SchemaDesignResponse {
  status?: 'in_progress' | 'completed' | 'failed' | string;
  version?: number;
  isActive?: boolean;
  schema?: {
    nodes?: any[];
    relationships?: any[];
    summary?: string;
    constraints?: any[];
    indexes?: any[];
  };
  cypherDdl?: string;
}

interface SchemaDesignStageProps {
  caseId: string;
  onComplete: () => void;
}

export default function SchemaDesignStage({  caseId, onComplete }: SchemaDesignStageProps) {
  const [showSchema, setShowSchema] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [showAIPrompt, setShowAIPrompt] = useState(false);
  const [showEvaluation, setShowEvaluation] = useState(false);
  const [showVersionHistory, setShowVersionHistory] = useState(false);
  const [showVisualization, setShowVisualization] = useState(false);
  const [evaluation, setEvaluation] = useState<SchemaEvaluation | null>(null);

  // Fetch schema design status
  const { data: schemaDesign, isLoading, refetch } = useQuery<SchemaDesignResponse>({
    queryKey: ['schemaDesign', caseId],
    queryFn: async () => {
      try {
        return await api.get(`/schema-design/cases/${ caseId}/schema`);
      } catch (error: any) {
        if (error?.response?.status === 404) return {} as SchemaDesignResponse;
        throw error;
      }
    },
    enabled: !!caseId,
    retry: false,
    // Poll every 10 seconds when generation is in progress (fallback if SSE fails)
    refetchInterval: (data) => {
      // Only poll if status is in_progress (cast to any for safety)
      return (data as any)?.status === 'in_progress' ? 10000 : false;
    },
  });

  // Memoize callbacks to prevent infinite re-renders
  const handleProgressComplete = useCallback(() => {
    setIsGenerating(false);
    refetch();
    toast.success('Neo4j schema design completed!');
  }, [refetch]);

  const handleProgressError = useCallback((error: Error) => {
    setIsGenerating(false);
    toast.error(error.message || 'Schema generation failed');
  }, []);

  // Progress streaming hook
  const progressStream = useProgressStream(
    isGenerating ? caseId : null,
    isGenerating,
    {
      onComplete: handleProgressComplete,
      onError: handleProgressError,
    }
  );

  // Mutation to start schema design
  const startDesignMutation = useMutation({
    mutationFn: async ({ forceRegenerate = false }: { forceRegenerate?: boolean } = {}) => {
      return await api.post(`/schema-design/cases/${ caseId}/generate`, {
        force_regenerate: forceRegenerate
      });
    },
    onMutate: () => {
      // Reset progress stream before starting
      progressStream.reset();
    },
    onSuccess: () => {
      // Start streaming AFTER generation has been triggered successfully
      setIsGenerating(true);
      toast.success('Schema design started! This may take a few minutes...');
    },
    onError: (error: any) => {
      setIsGenerating(false);
      toast.error(error?.response?.data?.detail || 'Failed to start schema design');
    },
  });

  // Mutation to update schema design
  const updateSchemaMutation = useMutation({
    mutationFn: async (updatedSchema: any) => {
      return await api.put(`/schema-design/cases/${ caseId}/schema`, updatedSchema);
    },
    onSuccess: () => {
      setIsEditing(false);
      refetch();
      toast.success('Schema updated successfully!');
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.detail || 'Failed to update schema');
    },
  });

  // Define status flags before using them
  const isInProgress = schemaDesign?.status === 'in_progress';
  const isCompleted = schemaDesign?.status === 'completed';
  const isFailed = schemaDesign?.status === 'failed';

  // Helper to detect if any schema data exists (since query returns an object)
  const hasSchemaData = !!(schemaDesign && (schemaDesign.status || schemaDesign.schema || schemaDesign.cypherDdl));

  // Query to fetch latest evaluation
  const { data: latestEvaluation, refetch: refetchEvaluation } = useQuery({
    queryKey: ['latestEvaluation', caseId],
    queryFn: async () => {
      try {
        return await api.get(`/schema-design/cases/${ caseId}/schema/evaluation/latest`);
      } catch (error: any) {
        if (error?.response?.status === 404) return null;
        throw error;
      }
    },
    enabled: isCompleted && !!caseId,
    retry: false,
  });

  // Query to fetch version history
  const { data: versionHistory, isLoading: isLoadingVersions } = useQuery<SchemaVersionsResponse>({
    queryKey: ['schemaVersionHistory', caseId],
    queryFn: async () => {
      return await api.get(`/schema-design/cases/${ caseId}/schema/versions`);
    },
    enabled: showVersionHistory && isCompleted && !!caseId,
    retry: false,
  });

  // Mutation to evaluate schema
  const evaluateSchemaMutation = useMutation({
    mutationFn: async () => {
      return await api.post(`/schema-design/cases/${ caseId}/schema/evaluate`);
    },
    onSuccess: (response: any) => {
      setEvaluation(response.evaluation);
      setShowEvaluation(true);
      refetchEvaluation(); // Refresh latest evaluation
      toast.success('Schema evaluation complete!');
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.detail || 'Failed to evaluate schema');
    },
  });

  // Fetch AI prompt when modal is opened
  const { data: aiPromptData, isLoading: isLoadingPrompt } = useQuery({
    queryKey: ['aiPrompt', caseId],
    queryFn: async () => {
      return await api.get(`/schema-design/cases/${ caseId}/ai-prompt`);
    },
    enabled: showAIPrompt && !!caseId,
    retry: false,
  });

  // Check if we need to show "Generate Schema" button
  // Show when: no schema data OR status is pending (new project)
  const shouldShowGenerateButton = !hasSchemaData || schemaDesign?.status === 'pending';

  return (
    <div className="space-y-6">
      {/* Schema Design Status - Show for new projects or when no schema exists */}
      {shouldShowGenerateButton && !isLoading && !isInProgress && !isGenerating && (
        <Card className="border-l-4 border-l-primary-500">
          <CardBody>
            <div className="flex items-start gap-4">
              <div className="p-3 bg-primary-100 rounded-lg">
                <Database className="w-6 h-6 text-primary-600" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-neutral-900 mb-2">
                  Start Your Schema Design
                </h3>
                <p className="text-neutral-600 mb-4">
                  Business Transformation Studio will analyze all your data requirements, executive needs, and system landscape to design an optimized Neo4j graph schema. This includes:
                </p>
                <ul className="space-y-2 text-sm text-neutral-700 mb-4">
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                    <span>Node labels for all master and transactional data entities</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                    <span>Relationship types that model business logic</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                    <span>Property definitions with data types and constraints</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                    <span>Indexes for optimal query performance</span>
                  </li>
                </ul>
                <Button
                  onClick={() => startDesignMutation.mutate({ forceRegenerate: false })}
                  loading={startDesignMutation.isPending}
                  disabled={startDesignMutation.isPending}
                >
                  <Sparkles className="w-4 h-4" />
                  Generate Schema
                </Button>
              </div>
            </div>
          </CardBody>
        </Card>
      )}

      {(isInProgress || isGenerating) && (
        <Card className="border-l-4 border-l-amber-500">
          <CardHeader>
            <div className="flex items-center gap-3">
              <Loader2 className="w-6 h-6 text-amber-600 animate-spin" />
              <div>
                <h3 className="text-lg font-semibold text-neutral-900">
                  Designing Schema...
                </h3>
                <p className="text-sm text-neutral-600">
                  Analyzing your data requirements and creating an optimized graph schema.
                </p>
              </div>
            </div>
          </CardHeader>
          <CardBody>
            {progressStream.updates.length > 0 ? (
              <ProgressDisplay
                updates={progressStream.updates}
                currentProgress={progressStream.currentProgress}
              />
            ) : (
              <div className="flex items-center justify-center py-8">
                <div className="text-center">
                  <Loader2 className="w-8 h-8 text-primary-600 animate-spin mx-auto mb-3" />
                  <p className="text-sm text-neutral-600">Connecting to generation service...</p>
                </div>
              </div>
            )}
          </CardBody>
        </Card>
      )}

      {isFailed && (
        <Card className="border-l-4 border-l-red-500">
          <CardBody>
            <div className="flex items-start justify-between">
              <div className="flex items-start gap-4">
                <div className="p-3 bg-red-100 rounded-lg">
                  <AlertCircle className="w-6 h-6 text-red-600" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-neutral-900 mb-2">
                    Schema Design Failed
                  </h3>
                  <p className="text-neutral-600 mb-4">
                    An error occurred while designing the schema. Please try again.
                  </p>
                  <Button
                    onClick={() => startDesignMutation.mutate({ forceRegenerate: true })}
                    loading={startDesignMutation.isPending}
                    disabled={startDesignMutation.isPending}
                  >
                    <PlayCircle className="w-4 h-4" />
                    Retry Schema Design
                  </Button>
                </div>
              </div>
            </div>
          </CardBody>
        </Card>
      )}

      {isCompleted && schemaDesign && (
        <div className="space-y-6">
          <Card className="border-l-4 border-l-green-500">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <CheckCircle2 className="w-6 h-6 text-green-600" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <h2 className="text-xl font-bold text-neutral-900">Schema Design Complete</h2>
                      <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-green-100 text-green-800">
                        <GitBranch className="w-3 h-3" />
                        v{schemaDesign.version || 1}
                        {schemaDesign.isActive && (
                          <span className="ml-1 px-1.5 py-0.5 rounded-full bg-green-600 text-white text-[10px]">LATEST</span>
                        )}
                      </span>
                    </div>
                    <p className="text-sm text-neutral-600 mt-1">
                      {schemaDesign.schema?.nodes?.length || 0} node types, {schemaDesign.schema?.relationships?.length || 0} relationship types
                      {(schemaDesign.schema?.constraints?.length || 0) > 0 && `, ${schemaDesign.schema?.constraints?.length} constraints`}
                      {(schemaDesign.schema?.indexes?.length || 0) > 0 && `, ${schemaDesign.schema?.indexes?.length} indexes`}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    onClick={() => setShowVersionHistory(true)}
                  >
                    <History className="w-4 h-4" />
                    History
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => setShowVisualization(!showVisualization)}
                  >
                    <Network className="w-4 h-4" />
                    {showVisualization ? 'Hide' : 'Visualize'} Schema
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => setShowSchema(!showSchema)}
                  >
                    <Eye className="w-4 h-4" />
                    {showSchema ? 'Hide' : 'View'} DDL
                  </Button>
                </div>
              </div>
            </CardHeader>

            {/* Schema Editor */}
                {isEditing && schemaDesign?.schema && (
              <CardBody>
                <SchemaEditor
                  schema={schemaDesign?.schema as any}
                  onSave={(updatedSchema) => updateSchemaMutation.mutate(updatedSchema)}
                  onCancel={() => setIsEditing(false)}
                />
              </CardBody>
            )}

            {/* Schema View */}
            {!isEditing && showSchema && (
              <CardBody>
                {/* Cypher DDL Script */}
                {schemaDesign.cypherDdl && (
                  <div className="mb-6">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="text-sm font-semibold text-neutral-700">Cypher DDL Script</h4>
                      <button
                        onClick={() => {
                          navigator.clipboard.writeText(schemaDesign.cypherDdl || '');
                          toast.success('Cypher DDL copied to clipboard!');
                        }}
                        className="text-xs px-3 py-1.5 bg-primary-100 text-primary-700 hover:bg-primary-200 rounded transition-colors"
                      >
                        Copy to Clipboard
                      </button>
                    </div>
                    <div className="bg-neutral-900 rounded-lg p-4 overflow-x-auto">
                      <pre className="text-sm text-neutral-100 font-mono whitespace-pre">
                        <code>{schemaDesign.cypherDdl}</code>
                      </pre>
                    </div>
                  </div>
                )}

                {/* Schema Summary */}
                {schemaDesign.schema?.summary && (
                  <div className="mb-6">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="text-sm font-semibold text-neutral-700">Schema Overview</h4>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => setShowAIPrompt(true)}
                          className="flex items-center gap-1.5 text-xs px-3 py-1.5 bg-secondary-100 text-secondary-700 hover:bg-secondary-200 rounded transition-colors"
                        >
                          <FileText className="w-3.5 h-3.5" />
                          View Generation Prompt
                        </button>
                        <button
                          onClick={() => setIsEditing(true)}
                          className="flex items-center gap-1.5 text-xs px-3 py-1.5 bg-primary-100 text-primary-700 hover:bg-primary-200 rounded transition-colors"
                        >
                          <Edit2 className="w-3.5 h-3.5" />
                          Edit Schema
                        </button>
                      </div>
                    </div>
                    <p className="text-sm text-neutral-600">{schemaDesign.schema.summary}</p>
                  </div>
                )}

                {/* Node Types */}
                {schemaDesign.schema?.nodes && schemaDesign.schema.nodes.length > 0 && (
                  <div className="mb-6">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="text-sm font-semibold text-neutral-700">Node Types</h4>
                      <div className="flex items-center gap-3 text-xs text-neutral-600">
                        <span className="flex items-center gap-1">
                          <Key className="w-3 h-3 text-amber-600" />
                          <span>Unique</span>
                        </span>
                        <span className="flex items-center gap-1">
                          <Search className="w-3 h-3 text-blue-600" />
                          <span>Indexed</span>
                        </span>
                        <span className="flex items-center gap-1">
                          <Lock className="w-3 h-3 text-red-600" />
                          <span>Required</span>
                        </span>
                      </div>
                    </div>
                    <div className="space-y-3">
                      {schemaDesign.schema.nodes.map((node: any, idx: number) => (
                        <div key={idx} className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                          <div className="flex items-start justify-between mb-2">
                            <h5 className="font-medium text-neutral-900">{node.label}</h5>
                            <span className="text-xs px-2 py-1 bg-purple-100 text-purple-700 rounded-full">
                              {node.category || 'Node'}
                            </span>
                          </div>
                          <p className="text-sm text-neutral-700 mb-3">{node.description}</p>
                          {node.properties && node.properties.length > 0 && (
                            <div>
                              <p className="text-xs font-medium text-neutral-700 mb-2">Properties:</p>
                              <div className="flex flex-wrap gap-2">
                                {node.properties.map((prop: any, i: number) => (
                                  <span key={i} className="text-xs px-2 py-1 bg-white rounded border border-purple-300 text-purple-800 flex items-center gap-1.5">
                                    <span className="font-medium">{prop.name}</span>
                                    <span className="text-purple-600">: {prop.type}</span>
                                    {(prop.unique || prop.indexed || prop.required) && (
                                      <span className="flex items-center gap-1 ml-1 pl-1.5 border-l border-purple-300">
                                        {prop.unique && (
                                          <span title="Unique constraint">
                                            <Key className="w-3 h-3 text-amber-600" />
                                          </span>
                                        )}
                                        {prop.indexed && (
                                          <span title="Indexed">
                                            <Search className="w-3 h-3 text-blue-600" />
                                          </span>
                                        )}
                                        {prop.required && (
                                          <span title="Required">
                                            <Lock className="w-3 h-3 text-red-600" />
                                          </span>
                                        )}
                                      </span>
                                    )}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Relationships */}
                {schemaDesign.schema?.relationships && schemaDesign.schema.relationships.length > 0 && (
                  <div>
                    <h4 className="text-sm font-semibold text-neutral-700 mb-3">Relationships</h4>
                    <div className="space-y-3">
                      {schemaDesign.schema.relationships.map((rel: any, idx: number) => (
                        <div key={idx} className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                          <div className="flex items-center gap-2 mb-2">
                            <span className="font-medium text-neutral-900">({rel.from})</span>
                            <span className="text-sm px-2 py-1 bg-blue-100 text-blue-700 rounded">
                              -{rel.type}→
                            </span>
                            <span className="font-medium text-neutral-900">({rel.to})</span>
                          </div>
                          <p className="text-sm text-neutral-700">{rel.description}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </CardBody>
            )}

            <CardFooter>
              <div className="flex items-center justify-between w-full">
                <div className="flex items-center gap-3">
                  <Button
                    variant="outline"
                    onClick={() => startDesignMutation.mutate({ forceRegenerate: true })}
                    loading={startDesignMutation.isPending}
                    disabled={startDesignMutation.isPending}
                  >
                    <Sparkles className="w-4 h-4" />
                    Regenerate Schema
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => evaluateSchemaMutation.mutate()}
                    loading={evaluateSchemaMutation.isPending}
                    disabled={evaluateSchemaMutation.isPending}
                  >
                    <Target className="w-4 h-4" />
                    {evaluation ? 'Re-evaluate' : 'Evaluate'} Schema
                  </Button>
                  {latestEvaluation && !showEvaluation && (
                    <Button
                      variant="outline"
                      onClick={() => {
                        setEvaluation(latestEvaluation);
                        setShowEvaluation(true);
                      }}
                    >
                      <Eye className="w-4 h-4" />
                      View Latest Evaluation
                    </Button>
                  )}
                </div>
                <Button onClick={onComplete}>
                  Continue to Data Files
                </Button>
              </div>
            </CardFooter>
          </Card>

          {/* Evaluation Results */}
          {showEvaluation && evaluation && (
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-blue-100 rounded-lg">
                      <Target className="w-6 h-6 text-blue-600" />
                    </div>
                    <div>
                      <h2 className="text-xl font-bold text-neutral-900">Schema Evaluation</h2>
                      <p className="text-sm text-neutral-600">
                        Assessment against executive questions and business context
                      </p>
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    onClick={() => setShowEvaluation(!showEvaluation)}
                  >
                    {showEvaluation ? 'Hide' : 'Show'} Evaluation
                  </Button>
                </div>
              </CardHeader>
              <CardBody>
                <SchemaEvaluationResults
                  evaluation={evaluation}
                  currentSchemaVersion={schemaDesign?.version}
                  onSchemaRefined={(refinedSchema) => {
                    // Automatically refresh schema and evaluation data after refinement
                    refetch();
                    refetchEvaluation();
                  }}
                />
              </CardBody>
            </Card>
          )}

          {/* Schema Visualization */}
          {showVisualization && schemaDesign?.schema && (
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-purple-100 rounded-lg">
                      <Network className="w-6 h-6 text-purple-600" />
                    </div>
                    <div>
                      <h2 className="text-xl font-bold text-neutral-900">Schema Visualization</h2>
                      <p className="text-sm text-neutral-600">
                        Interactive graph visualization with editing capabilities
                      </p>
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    onClick={() => setShowVisualization(!showVisualization)}
                  >
                    {showVisualization ? 'Hide' : 'Show'} Visualization
                  </Button>
                </div>
              </CardHeader>
              <CardBody>
                <SchemaVisualizer
                  schema={{
                    ...schemaDesign.schema,
                    version: schemaDesign.version || 1,
                    ddl: schemaDesign.cypherDdl || '',
                    constraints: schemaDesign.schema?.constraints || [],
                    indexes: schemaDesign.schema?.indexes || [],
                  } as any}
                  evaluation={latestEvaluation || evaluation || undefined}
                  onSchemaUpdate={(updatedSchema) => {
                    updateSchemaMutation.mutate(updatedSchema);
                  }}
                />
              </CardBody>
            </Card>
          )}
        </div>
      )}

      {/* Version History Modal */}
      <Modal
        isOpen={showVersionHistory}
        onClose={() => setShowVersionHistory(false)}
        title="Schema Version History"
        size="xl"
      >
        {isLoadingVersions ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 text-primary-600 animate-spin" />
          </div>
        ) : versionHistory?.versions && versionHistory.versions.length > 0 ? (
          <div className="space-y-4">
            <div className="text-sm text-neutral-600 mb-4">
              Showing {versionHistory.versions.length} version{versionHistory.versions.length !== 1 ? 's' : ''} for this project
            </div>
            {versionHistory.versions.map((version, idx) => (
              <div
                key={version.version}
                className={`border rounded-lg p-4 ${
                  version.isActive
                    ? 'border-green-300 bg-green-50'
                    : 'border-neutral-200 bg-white'
                }`}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-lg ${
                      version.isActive ? 'bg-green-100' : 'bg-neutral-100'
                    }`}>
                      <GitBranch className={`w-5 h-5 ${
                        version.isActive ? 'text-green-600' : 'text-neutral-600'
                      }`} />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h4 className="font-semibold text-neutral-900">
                          Version {version.version}
                        </h4>
                        {version.isActive && (
                          <span className="px-2 py-0.5 rounded-full bg-green-600 text-white text-xs font-semibold">
                            LATEST
                          </span>
                        )}
                        {version.parentVersion && (
                          <span className="text-xs text-neutral-500">
                            (refined from v{version.parentVersion})
                          </span>
                        )}
                      </div>
                      {version.refinementReason && (
                        <p className="text-sm text-neutral-600 mt-1">
                          {version.refinementReason}
                        </p>
                      )}
                    </div>
                  </div>
                  <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${
                    version.status === 'completed'
                      ? 'bg-green-100 text-green-800'
                      : version.status === 'in_progress'
                      ? 'bg-amber-100 text-amber-800'
                      : 'bg-red-100 text-red-800'
                  }`}>
                    {version.status}
                  </span>
                </div>

                {/* Version Metrics */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-3">
                  <div className="bg-white border border-neutral-200 rounded-lg p-3">
                    <div className="flex items-center gap-2 mb-1">
                      <Database className="w-4 h-4 text-purple-600" />
                      <span className="text-xs text-neutral-600">Nodes</span>
                    </div>
                    <p className="text-lg font-semibold text-neutral-900">
                      {version.nodeCount}
                    </p>
                  </div>
                  <div className="bg-white border border-neutral-200 rounded-lg p-3">
                    <div className="flex items-center gap-2 mb-1">
                      <GitBranch className="w-4 h-4 text-blue-600" />
                      <span className="text-xs text-neutral-600">Relationships</span>
                    </div>
                    <p className="text-lg font-semibold text-neutral-900">
                      {version.relationshipCount}
                    </p>
                  </div>
                  <div className="bg-white border border-neutral-200 rounded-lg p-3">
                    <div className="flex items-center gap-2 mb-1">
                      <Lock className="w-4 h-4 text-red-600" />
                      <span className="text-xs text-neutral-600">Constraints</span>
                    </div>
                    <p className="text-lg font-semibold text-neutral-900">
                      {version.constraintCount}
                    </p>
                  </div>
                  <div className="bg-white border border-neutral-200 rounded-lg p-3">
                    <div className="flex items-center gap-2 mb-1">
                      <Search className="w-4 h-4 text-amber-600" />
                      <span className="text-xs text-neutral-600">Indexes</span>
                    </div>
                    <p className="text-lg font-semibold text-neutral-900">
                      {version.indexCount}
                    </p>
                  </div>
                </div>

                {/* Timestamps */}
                <div className="flex items-center gap-4 text-xs text-neutral-600">
                  <div className="flex items-center gap-1">
                    <Calendar className="w-3 h-3" />
                    <span>Created: {new Date(version.createdAt).toLocaleString()}</span>
                  </div>
                  {version.completedAt && (
                    <div className="flex items-center gap-1">
                      <CheckCircle2 className="w-3 h-3" />
                      <span>Completed: {new Date(version.completedAt).toLocaleString()}</span>
                    </div>
                  )}
                  {version.qualityScore && (
                    <div className="flex items-center gap-1">
                      <Target className="w-3 h-3" />
                      <span>Quality: {version.qualityScore}/100</span>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <History className="w-12 h-12 text-neutral-400 mx-auto mb-3" />
              <p className="text-sm text-neutral-600">No version history available</p>
            </div>
          </div>
        )}
      </Modal>

      {/* AI Prompt Modal */}
      {showAIPrompt && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b border-neutral-200">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-secondary-100 rounded-lg">
                  <FileText className="w-5 h-5 text-secondary-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-neutral-900">Generation Prompt</h3>
                  <p className="text-sm text-neutral-600">Full prompt and context used for schema generation</p>
                </div>
              </div>
              <button
                onClick={() => setShowAIPrompt(false)}
                className="p-2 hover:bg-neutral-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-neutral-600" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="flex-1 overflow-y-auto p-6">
              {isLoadingPrompt ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 text-primary-600 animate-spin" />
                </div>
              ) : aiPromptData ? (
                <div className="space-y-6">
                  {/* Context Details */}
                  {aiPromptData.contextDetails && (
                    <div>
                      <h4 className="text-sm font-semibold text-neutral-700 mb-3">Context Information</h4>
                      <div className="grid grid-cols-2 gap-4 mb-4">
                        {aiPromptData.contextDetails.business_domain && (
                          <div className="bg-neutral-50 border border-neutral-200 rounded-lg p-3">
                            <p className="text-xs text-neutral-600 mb-1">Business Domain</p>
                            <p className="text-sm font-medium text-neutral-900">{aiPromptData.contextDetails.business_domain}</p>
                          </div>
                        )}
                        {aiPromptData.contextDetails.company_name && (
                          <div className="bg-neutral-50 border border-neutral-200 rounded-lg p-3">
                            <p className="text-xs text-neutral-600 mb-1">Company</p>
                            <p className="text-sm font-medium text-neutral-900">{aiPromptData.contextDetails.company_name}</p>
                          </div>
                        )}
                        {aiPromptData.contextDetails.master_data_count !== undefined && (
                          <div className="bg-neutral-50 border border-neutral-200 rounded-lg p-3">
                            <p className="text-xs text-neutral-600 mb-1">Master Data Entities</p>
                            <p className="text-sm font-medium text-neutral-900">{aiPromptData.contextDetails.master_data_count}</p>
                          </div>
                        )}
                        {aiPromptData.contextDetails.transactional_data_count !== undefined && (
                          <div className="bg-neutral-50 border border-neutral-200 rounded-lg p-3">
                            <p className="text-xs text-neutral-600 mb-1">Transactional Data Entities</p>
                            <p className="text-sm font-medium text-neutral-900">{aiPromptData.contextDetails.transactional_data_count}</p>
                          </div>
                        )}
                        {aiPromptData.contextDetails.systems_count !== undefined && (
                          <div className="bg-neutral-50 border border-neutral-200 rounded-lg p-3">
                            <p className="text-xs text-neutral-600 mb-1">Systems</p>
                            <p className="text-sm font-medium text-neutral-900">{aiPromptData.contextDetails.systems_count}</p>
                          </div>
                        )}
                        {aiPromptData.contextDetails.executive_needs_count !== undefined && (
                          <div className="bg-neutral-50 border border-neutral-200 rounded-lg p-3">
                            <p className="text-xs text-neutral-600 mb-1">Executive Questions</p>
                            <p className="text-sm font-medium text-neutral-900">{aiPromptData.contextDetails.executive_needs_count}</p>
                          </div>
                        )}
                      </div>

                      {/* Business Context */}
                      {aiPromptData.contextDetails.business_context && (
                        <div className="mb-4">
                          <p className="text-xs text-neutral-600 mb-2">Business Context</p>
                          <div className="bg-white border border-neutral-200 rounded-lg p-3 max-h-32 overflow-y-auto">
                            <p className="text-sm text-neutral-700 whitespace-pre-wrap">{aiPromptData.contextDetails.business_context}</p>
                          </div>
                        </div>
                      )}

                      {/* Executive Needs */}
                      {aiPromptData.contextDetails.executive_needs_list && aiPromptData.contextDetails.executive_needs_list.length > 0 && (
                        <div>
                          <p className="text-xs text-neutral-600 mb-2">Executive Questions</p>
                          <div className="bg-white border border-neutral-200 rounded-lg p-3 max-h-48 overflow-y-auto space-y-2">
                            {aiPromptData.contextDetails.executive_needs_list.map((need: any, i: number) => (
                              <div key={i} className="flex items-start gap-2 pb-2 border-b border-neutral-100 last:border-0 last:pb-0">
                                <span className="text-xs font-medium text-secondary-600 flex-shrink-0 mt-0.5">
                                  [{need.stakeholder}]
                                </span>
                                <span className="text-xs text-neutral-700">{need.question}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Full Prompt */}
                  {aiPromptData.prompt && (
                    <div>
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="text-sm font-semibold text-neutral-700">Full Prompt</h4>
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-neutral-600">
                            {aiPromptData.promptLength?.toLocaleString()} characters
                          </span>
                          <button
                            onClick={() => {
                              navigator.clipboard.writeText(aiPromptData.prompt);
                              toast.success('Prompt copied to clipboard!');
                            }}
                            className="text-xs px-3 py-1.5 bg-secondary-100 text-secondary-700 hover:bg-secondary-200 rounded transition-colors"
                          >
                            Copy
                          </button>
                        </div>
                      </div>
                      <div className="bg-neutral-900 rounded-lg p-4 max-h-96 overflow-y-auto">
                        <pre className="text-xs text-neutral-100 font-mono whitespace-pre-wrap">
                          {aiPromptData.prompt}
                        </pre>
                      </div>
                    </div>
                  )}

                  {/* Metadata */}
                  {aiPromptData.createdAt && (
                    <div className="pt-4 border-t border-neutral-200">
                      <p className="text-xs text-neutral-600">
                        Generated on {new Date(aiPromptData.createdAt).toLocaleString()}
                      </p>
                    </div>
                  )}
                </div>
              ) : (
                <div className="flex items-center justify-center py-12">
                  <div className="text-center">
                    <AlertCircle className="w-12 h-12 text-neutral-400 mx-auto mb-3" />
                    <p className="text-sm text-neutral-600">No prompt data found</p>
                  </div>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="flex items-center justify-end gap-3 p-6 border-t border-neutral-200">
              <Button
                variant="outline"
                onClick={() => setShowAIPrompt(false)}
              >
                Close
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
